"use client";

import { motion, AnimatePresence } from "framer-motion";
import { useGetProgress } from "@/lib/api/generated";
import { Icon } from "@/components/ui/icon";
import { usePipelineUiStore } from "@/features/jobs/store/pipeline/ui";

interface ProcessingProgressBarProps {
  jobId: string;
}

export function ProcessingProgressBar({ jobId }: ProcessingProgressBarProps) {
  const openImportSheet = usePipelineUiStore((s) => s.openImportSheet);

  // Polling is driven by `useImportProgressSync` in the job layout — this
  // observer just reads the shared cache so we don't double-poll.
  const { data: status } = useGetProgress(jobId);

  const isActive = !!status && status.total > 0 && status.processing > 0;

  return (
    <AnimatePresence>
      {isActive && status && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 36, opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="overflow-hidden shrink-0"
        >
          {/* Whole bar is clickable — jumps to the ProfileImportSheet's
              Processing tab so the user can inspect individual rows.   */}
          <button
            type="button"
            onClick={() => openImportSheet("processing")}
            aria-label="Open processing imports"
            className="group relative flex h-9 w-full cursor-pointer items-center justify-between overflow-hidden bg-primary px-4 text-left text-primary-foreground transition-colors hover:bg-primary/90"
          >
            <div className="relative z-10 flex items-center gap-1.5">
              <div className="opacity-80">
                <Icon name="IconPeopleCircle" size={16} className="text-primary-foreground" />
              </div>
              <span className="text-sm font-medium">
                {status.processing} Profile{status.processing === 1 ? "" : "s"} Being Processed
              </span>
            </div>

            <div className="relative z-10 flex items-center gap-[3px]">
              <EqBar delay="0s" />
              <EqBar delay="0.15s" />
              <EqBar delay="0.3s" />
              <EqBar delay="0.45s" />
            </div>
          </button>

          <style jsx>{`
            @keyframes eqPulse {
              0%,
              100% {
                transform: scaleY(0.35);
                opacity: 0.6;
              }
              50% {
                transform: scaleY(1);
                opacity: 1;
              }
            }
          `}</style>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function EqBar({ delay }: { delay: string }) {
  return (
    <span
      aria-hidden
      className="block h-3.5 w-[2px] origin-center rounded-full bg-primary-foreground/85"
      style={{
        animation: "eqPulse 1s ease-in-out infinite",
        animationDelay: delay,
      }}
    />
  );
}
